import PropTypes from "prop-types";
import React, { useState } from "react";

import { fireTrack } from "@swa-ui/analytics";
import { SMALL, useDeviceInfo, XLARGE } from "@swa-ui/browser";
import { Heading, Layout, ListScroller } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { CallToAction, InformationTipAdapter, PlacementFactory } from "@swa-ui/placements";
import { TextContent } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { DestinationByOriginCard } from "./DestinationByOriginCard";
import styles from "./DestinationByOriginPlacement.module.scss";

export const DestinationByOriginPlacement = (props) => {
  const { placement, placementData = {} } = props;
  const { backgroundColor, calloutText, callToAction, disclaimer, items, title } = placement ?? {};
  const { isLargeOrXlarge, screenSize } = useDeviceInfo();
  const [imageHeight, setImageHeight] = useState(0);

  return screenSize === SMALL ? renderContent() : renderContentMediumLargeXlarge();

  function renderContent() {
    return (
      <div {...getContainerProps()}>
        <Layout bottom="medium" top="large">
          {renderCalloutText()}
          {renderHeading()}
          {renderDisclaimer()}
        </Layout>
        {items && items.length && <ListScroller {...getListScrollerProps()} />}
        {renderCallToAction()}
      </div>
    );
  }

  function renderContentMediumLargeXlarge() {
    return (
      <div {...getContainerProps()}>
        <Layout {...getLayoutProps()}>
          {renderCalloutText()}
          <Layout className={styles.headingContainer}>
            {renderHeading()}
            {renderCallToAction()}
          </Layout>
          {renderDisclaimer()}
        </Layout>
        {items && items.length && <ListScroller {...getListScrollerProps()} />}
      </div>
    );
  }

  function renderCalloutText() {
    return calloutText && <TextContent {...calloutText} />;
  }

  function renderHeading() {
    return title && <Heading {...getHeadingProps()} />;
  }

  function renderCallToAction() {
    return (
      callToAction && <CallToAction {...callToAction} paddingForFocus={screenSize !== SMALL} />
    );
  }

  function renderDisclaimer() {
    return (
      disclaimer && (
        <div className={styles.disclaimer}>
          <TextContent value={disclaimer.value} />
          {disclaimer?.infoBox && <InformationTipAdapter {...getInformationTipProps()} />}
        </div>
      )
    );
  }

  function getContainerProps() {
    return { style: { backgroundColor: backgroundColor } };
  }

  function getLayoutProps() {
    return {
      bottom: "medium",
      center: screenSize === XLARGE,
      top: "large",
    };
  }

  function getHeadingProps() {
    return {
      children: <TextContent {...title} />,
      className: styles.heading,
      headingLevel: 2,
      styleLevel: title.styleLevel,
    };
  }

  function getInformationTipProps() {
    const { infoBox } = disclaimer;
    const { infoBoxSize, placementId } = infoBox;

    return {
      className: styles.informationTip,
      content: <PlacementFactory id={placementId} />,
      infoBoxSize,
      location: "below",
      width: "small",
    };
  }

  function getListScrollerProps() {
    return {
      "aria-label-current-page": i18n("DestinationByOriginPlacement__ARIA_LABEL_CURRENT_PAGE"),
      "aria-label-focus-change-next": i18n(
        "DestinationByOriginPlacement__ARIA_LABEL_FOCUS_CHANGE_NEXT"
      ),
      "aria-label-focus-change-previous": i18n(
        "DestinationByOriginPlacement__ARIA_LABEL_FOCUS_CHANGE_PREVIOUS"
      ),
      "aria-label-next": i18n("DestinationByOriginPlacement__ARIA_LABEL_NEXT"),
      "aria-label-page-count": i18n("DestinationByOriginPlacement__ARIA_LABEL_PAGE_COUNT"),
      "aria-label-previous": i18n("DestinationByOriginPlacement__ARIA_LABEL_PREVIOUS"),
      ...(imageHeight > 0 && { yArrowOffset: imageHeight / 2 - 18 }),
      className: classNames({ [styles.center]: screenSize === XLARGE }),
      items: items.map((item, index) => (
        <DestinationByOriginCard
          analyticsData={placementData.lfmaDetails?.[index]}
          item={item}
          key={item}
          onImageLoad={handleImageLoad}
        />
      )),
      onClick: handleListScrollerClick,
      showPositionIndicator: isLargeOrXlarge,
    };
  }

  function handleListScrollerClick() {
    const analyticsValue = placementData?.analyticsValue;

    analyticsValue && fireTrack("squid", { page_description: analyticsValue });
  }

  function handleImageLoad(value) {
    setImageHeight(value[0].contentRect.height);
  }
};

DestinationByOriginPlacement.displayName = "DestinationByOriginPlacement";
DestinationByOriginPlacement.propTypes = {
  /** Object that contains placement props, including backgroundColor, title etc */
  placement: PropTypes.shape({
    /** String that contains background color for this placement. */
    backgroundColor: PropTypes.string.isRequired,

    /** Object that contains callout copy text props. */
    calloutText: PropTypes.shape({
      color: PropTypes.string,
      fontSize: PropTypes.string,
      fontWeight: PropTypes.string,
      value: PropTypes.string.isRequired,
    }),

    /** Object that contains link/button props. */
    callToAction: PropTypes.object,

    /** Object that contains legal disclaimer text props. */
    disclaimer: PropTypes.shape({
      infoBox: PropTypes.shape({
        infoBoxSize: PropTypes.string,
        placementId: PropTypes.string.isRequired,
      }),
      value: PropTypes.string.isRequired,
    }),

    /** Array that contains list of content items to be rendered as DestinationByOrigin card components. */
    items: PropTypes.array.isRequired,

    /** Object that contains analytics data for the placement. */
    placementData: PropTypes.object,

    /** Object that contains headline title props. */
    title: PropTypes.shape({
      color: PropTypes.string,
      fontSize: PropTypes.string,
      fontWeight: PropTypes.string,
      value: PropTypes.string.isRequired,
    }),
  }),
};
